import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  CfoCloseEvidenceItem,
  CfoCloseEffectiveness,
  CfoCloseFinding,
  CfoCloseHistoryPeriod,
  CfoCloseHistoryResponse,
  CfoCloseReview,
  CfoCloseTask,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import { CfoInteriorPage } from './CfoInteriorPage'
import { CFO_SURFACES } from './cfoTelemetry'
import type { CfoNavProfile } from './cfoNav'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import './CfoClosePage.css'

type CloseTab = 'command' | 'checklist' | 'reconciliations' | 'findings' | 'review' | 'history'

type CustomCloseTaskInput = {
  title: string
  description: string
  facet: string
  due_business_day: number
  evidence_required: boolean
  recurrence: 'one_time' | 'monthly'
}

export type CfoClosePageProps = {
  appApi: ServerApiClient
  workbookId: string | null
  onNavSelect: (navId: string) => void
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

const TABS: Array<{ id: CloseTab; label: string }> = [
  { id: 'command', label: 'Command center' },
  { id: 'checklist', label: 'Checklist' },
  { id: 'reconciliations', label: 'Reconciliations' },
  { id: 'findings', label: 'Findings & reasonableness' },
  { id: 'review', label: 'Review & sign-off' },
  { id: 'history', label: 'History' },
]

const titleCase = (value: unknown) => String(value ?? '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase())

const formatPeriod = (value: string | null | undefined) => {
  if (!value) return 'No period'
  const date = new Date(`${value.slice(0, 10)}T00:00:00`)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

const formatDate = (value: unknown) => {
  if (!value) return '—'
  const date = new Date(String(value))
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const money = (amount: number | null | undefined, currency = 'USD') => {
  if (amount == null || !Number.isFinite(amount)) return null
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount)
  } catch {
    return amount.toLocaleString()
  }
}

const requestKey = (prefix: string) => {
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `${prefix}:${id}`
}

const toneFor = (value: string) => {
  if (value === 'ready' || value === 'passed' || value === 'plausible' || value === 'complete' || value === 'locked' || value === 'improving') return 'good'
  if (value === 'blocked' || value === 'failed' || value === 'anomalous' || value === 'changed_after_lock' || value === 'declining') return 'bad'
  if (value === 'unavailable' || value === 'needs_attention' || value === 'ready_with_exceptions' || value === 'overdue') return 'warn'
  return 'neutral'
}

const severityOrder: Record<string, number> = { blocking: 0, high: 1, medium: 2, informational: 3 }

const roundOne = (value: number) => Math.round(value * 10) / 10

const businessDaysAfterPeriodEnd = (period: string, completedAt: string | null | undefined) => {
  if (!completedAt) return null
  const [year, month] = period.slice(0, 10).split('-').map(Number)
  const completed = new Date(completedAt)
  if (!year || !month || Number.isNaN(completed.getTime())) return null
  const cursor = new Date(Date.UTC(year, month, 0))
  const completedDate = new Date(Date.UTC(completed.getUTCFullYear(), completed.getUTCMonth(), completed.getUTCDate()))
  let businessDays = 0
  while (cursor < completedDate) {
    cursor.setUTCDate(cursor.getUTCDate() + 1)
    const weekday = cursor.getUTCDay()
    if (weekday !== 0 && weekday !== 6) businessDays += 1
  }
  return businessDays
}

const effectivenessFromPeriods = (periods: CfoCloseHistoryPeriod[]): CfoCloseEffectiveness => {
  const comparable = periods.filter((period) => period.close_business_day != null)
  const latest = comparable[0]
  if (!latest) return {
    trend: 'insufficient_history',
    comparable_periods: 0,
    rationale: 'Complete a close to establish the first effectiveness baseline.',
  }
  const latestSnapshot = {
    period: latest.period,
    close_business_day: latest.close_business_day,
    on_time_rate: latest.on_time_rate,
    late_tasks: latest.late_tasks,
    reopened: Boolean(latest.reopened_at),
    recurring_findings: latest.recurring_findings,
  }
  const previous = comparable[1]
  if (!previous) return {
    trend: 'insufficient_history',
    comparable_periods: 1,
    latest: latestSnapshot,
    rationale: 'One completed close is available; complete another month to measure improvement.',
  }
  const closeDayDelta = (latest.close_business_day || 0) - (previous.close_business_day || 0)
  const onTimeDelta = latest.on_time_rate == null || previous.on_time_rate == null
    ? null
    : roundOne(latest.on_time_rate - previous.on_time_rate)
  const lateTaskDelta = latest.late_tasks - previous.late_tasks
  const recurringFindingDelta = latest.recurring_findings - previous.recurring_findings
  const latestReopened = Boolean(latest.reopened_at)
  const previousReopened = Boolean(previous.reopened_at)
  const materialRegression = closeDayDelta > 1
    || (onTimeDelta != null && onTimeDelta < -5)
    || lateTaskDelta > 1
    || (!previousReopened && latestReopened)
  const measurableImprovement = closeDayDelta < 0
    || (onTimeDelta != null && onTimeDelta > 1)
    || lateTaskDelta < 0
    || (previousReopened && !latestReopened)
    || recurringFindingDelta < 0
  const trend = materialRegression ? 'declining' : measurableImprovement ? 'improving' : 'stable'
  return {
    trend,
    comparable_periods: comparable.length,
    latest: latestSnapshot,
    previous: {
      period: previous.period,
      close_business_day: previous.close_business_day,
      on_time_rate: previous.on_time_rate,
      late_tasks: previous.late_tasks,
      reopened: previousReopened,
      recurring_findings: previous.recurring_findings,
    },
    change: {
      close_business_days: closeDayDelta,
      on_time_rate_points: onTimeDelta,
      late_tasks: lateTaskDelta,
      recurring_findings: recurringFindingDelta,
    },
    rationale: trend === 'improving'
      ? 'The latest completed close finished faster or with stronger on-time execution, without a material control regression.'
      : trend === 'declining'
        ? 'The latest completed close shows a material timing, lateness, or reopening regression that needs attention.'
        : 'The latest two completed closes are operating within a stable timing and control range.',
    method: 'Compares completed closes only. Improving requires a measurable gain with no material timing, lateness, or reopening regression.',
  }
}

const historyPeriodFromReview = (
  review: CfoCloseReview,
  summary: Partial<CfoCloseHistoryPeriod> | undefined,
  recurringKeys: Set<string>,
): CfoCloseHistoryPeriod => {
  let completedTasks = 0
  let waivedTasks = 0
  let onTimeTasks = 0
  let lateTasks = 0
  let completionDayTotal = 0
  const taskCompletions = review.tasks.items.map((task) => {
    const completionBusinessDay = businessDaysAfterPeriodEnd(review.period.start, task.completed_at)
    const onTime = task.status === 'complete' && task.completed_at && task.due_date
      ? task.completed_at.slice(0, 10) <= task.due_date.slice(0, 10)
      : null
    if (task.status === 'complete') {
      completedTasks += 1
      if (onTime === true) onTimeTasks += 1
      if (onTime === false) lateTasks += 1
      if (completionBusinessDay != null) completionDayTotal += completionBusinessDay
    } else if (task.status === 'waived') {
      waivedTasks += 1
    }
    return {
      task_id: task.id || task.task_key,
      task_key: task.task_key,
      facet: task.facet,
      title: task.title,
      status: task.status,
      owner_user_id: task.owner_user_id,
      reviewer_user_id: task.reviewer_user_id,
      due_date: task.due_date,
      due_business_day: task.due_business_day,
      completed_at: task.completed_at,
      completion_business_day: completionBusinessDay,
      on_time: onTime,
      completion_event_count: task.completed_at ? 1 : 0,
    }
  })
  const totalTasks = taskCompletions.length
  const terminalTasks = completedTasks + waivedTasks
  const terminalDates = taskCompletions
    .filter((task) => (task.status === 'complete' || task.status === 'waived') && task.completed_at)
    .map((task) => String(task.completed_at))
    .sort()
  const closeCompletedAt = totalTasks > 0 && terminalTasks === totalTasks
    ? terminalDates[terminalDates.length - 1] || null
    : null
  const recurringFindings = review.findings.filter((finding) => recurringKeys.has(finding.stable_key)).length
  return {
    period: review.period.start,
    evidence_disposition: review.states.evidence_disposition,
    workflow_status: review.states.workflow_status,
    provider_lock_status: review.states.provider_lock_status,
    reasonableness: review.states.reasonableness,
    open_findings: summary?.open_findings ?? review.findings.filter((finding) => finding.lifecycle === 'open' || finding.lifecycle === 'reopened').length,
    recurring_findings: recurringFindings,
    open_tasks: totalTasks - terminalTasks,
    completed_tasks: completedTasks,
    waived_tasks: waivedTasks,
    total_tasks: totalTasks,
    completion_rate: totalTasks ? roundOne((terminalTasks / totalTasks) * 100) : null,
    on_time_tasks: onTimeTasks,
    late_tasks: lateTasks,
    on_time_rate: completedTasks ? roundOne((onTimeTasks / completedTasks) * 100) : null,
    average_completion_business_day: completedTasks ? roundOne(completionDayTotal / completedTasks) : null,
    close_completed_at: closeCompletedAt,
    close_business_day: businessDaysAfterPeriodEnd(review.period.start, closeCompletedAt),
    signoff_count: review.signoffs.length,
    reopened_at: summary?.reopened_at,
    updated_at: summary?.updated_at,
    task_completions: taskCompletions,
  }
}

async function loadCloseHistory(
  appApi: ServerApiClient,
  workbookId: string,
): Promise<CfoCloseHistoryResponse> {
  const response = await appApi.getCfoCloseHistory(workbookId)
  if (response.effectiveness && response.periods.every((period) => Array.isArray(period.task_completions))) return response

  // Compatibility window: the deployed v1 history has durable period summaries,
  // while each existing v2 review already carries the exact task timestamps.
  const summaries = response.periods.slice(0, 6)
  const reviews: CfoCloseReview[] = []
  for (const summary of summaries) reviews.push(await appApi.getCfoCloseReview(workbookId, summary.period))
  const keyPeriods = new Map<string, Set<string>>()
  for (const review of reviews) {
    for (const finding of review.findings) {
      const periods = keyPeriods.get(finding.stable_key) || new Set<string>()
      periods.add(review.period.start)
      keyPeriods.set(finding.stable_key, periods)
    }
  }
  const recurringKeys = new Set(Array.from(keyPeriods.entries()).filter(([, periods]) => periods.size > 1).map(([key]) => key))
  const periods = reviews.map((review) => historyPeriodFromReview(
    review,
    response.periods.find((period) => period.period === review.period.start),
    recurringKeys,
  ))
  return {
    schema_version: 'fg.close_history.v2.client_compat',
    workbook_id: response.workbook_id || workbookId,
    company_id: response.company_id || reviews[0]?.company.id || '',
    effectiveness: effectivenessFromPeriods(periods),
    periods,
    methodology: {
      close_completed_at: 'Latest completion timestamp after every task is complete or waived.',
      business_day: 'Weekdays after calendar period end; company holidays are not yet excluded.',
      on_time_rate: 'Completed tasks at or before their task deadline; waivers are shown separately.',
      compatibility: 'Task timing is derived from the governed period snapshots while the server history v2 release is pending.',
    },
  }
}

export function CfoClosePage({
  appApi,
  workbookId,
  onNavSelect,
  navProfile = 'full',
  runtimeProfile,
}: CfoClosePageProps) {
  const [periods, setPeriods] = useState<Array<{ period: string }>>([])
  const [selectedPeriod, setSelectedPeriod] = useState('')
  const [review, setReview] = useState<CfoCloseReview | null>(null)
  const [history, setHistory] = useState<CfoCloseHistoryResponse | null>(null)
  const [tab, setTab] = useState<CloseTab>('command')
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState('')
  const [error, setError] = useState('')
  const [focusedFacet, setFocusedFacet] = useState<string | null>(null)
  const [findingAction, setFindingAction] = useState<{ id: string; lifecycle: 'resolved' | 'accepted_exception' | 'reopened' } | null>(null)
  const [findingReason, setFindingReason] = useState('')
  const [signoffRole, setSignoffRole] = useState<'preparer' | 'reviewer' | 'attester'>('preparer')
  const [signoffAcknowledgement, setSignoffAcknowledgement] = useState('')

  const loadIndex = useCallback(async () => {
    if (!workbookId) return
    const response = await appApi.getAccountingClose(workbookId)
    setPeriods(response.periods || [])
    setSelectedPeriod((current) => current || response.periods?.[0]?.period || '')
  }, [appApi, workbookId])

  const loadReview = useCallback(async (period: string) => {
    if (!workbookId || !period) return
    const response = await appApi.getCfoCloseReview(workbookId, period)
    setReview(response)
  }, [appApi, workbookId])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    loadIndex()
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'Close periods could not be loaded.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [loadIndex])

  useEffect(() => {
    if (!selectedPeriod) return
    let cancelled = false
    setLoading(true)
    setError('')
    loadReview(selectedPeriod)
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'The close review could not be loaded.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [loadReview, selectedPeriod])

  useEffect(() => {
    if (tab !== 'history' || !workbookId) return
    loadCloseHistory(appApi, workbookId)
      .then((response) => setHistory(response))
      .catch((cause) => setError(cause instanceof Error ? cause.message : 'Close history could not be loaded.'))
  }, [appApi, tab, workbookId])

  const replaceReview = (next: CfoCloseReview) => {
    setReview(next)
    setError('')
  }
  const persisted = Boolean(review?.freshness.persisted_source_set_token)

  const runAnalysis = async () => {
    if (!workbookId || !selectedPeriod) return
    setWorking('run')
    setError('')
    try {
      const response = await appApi.runCfoCloseReview(workbookId, selectedPeriod, {
        expected_source_set_token: review?.freshness.source_set_token || null,
        idempotency_key: requestKey('close-run'),
      })
      replaceReview(response.review)
      await loadIndex()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The close analysis could not be completed.')
    } finally {
      setWorking('')
    }
  }

  const updateTask = async (task: CfoCloseTask, status: string) => {
    if (!workbookId || !review || !selectedPeriod) return
    if (!task.persisted) {
      setError('Run and pin the close review before updating checklist tasks.')
      return
    }
    setWorking(`task:${task.id}`)
    setError('')
    try {
      const response = await appApi.updateCfoCloseTask(workbookId, selectedPeriod, task.id || task.task_key, {
        status,
        expected_source_set_token: review.freshness.source_set_token,
        idempotency_key: requestKey(`task-${task.task_key}`),
        waiver_reason: status === 'waived' ? 'Advisory close exception accepted from the close workspace.' : null,
      })
      replaceReview(response.review)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The checklist task could not be updated.')
    } finally {
      setWorking('')
    }
  }

  const createCustomTask = async (input: CustomCloseTaskInput) => {
    if (!workbookId || !review || !selectedPeriod) return false
    if (!persisted) {
      setError('Run and pin the close review before adding checklist items.')
      return false
    }
    setWorking('custom-task')
    setError('')
    try {
      const response = await appApi.createCfoCloseCustomTask(workbookId, selectedPeriod, {
        ...input,
        expected_source_set_token: review.freshness.source_set_token,
        idempotency_key: requestKey('custom-task'),
      })
      replaceReview(response.review)
      return true
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The custom checklist item could not be added.')
      return false
    } finally {
      setWorking('')
    }
  }

  const submitFindingDisposition = async () => {
    if (!workbookId || !review || !selectedPeriod || !findingAction || !findingReason.trim()) return
    setWorking(`finding:${findingAction.id}`)
    try {
      const response = await appApi.dispositionCfoCloseFinding(workbookId, selectedPeriod, findingAction.id, {
        lifecycle: findingAction.lifecycle,
        reason: findingReason.trim(),
        expected_source_set_token: review.freshness.source_set_token,
        idempotency_key: requestKey(`finding-${findingAction.id}`),
      })
      replaceReview(response.review)
      setFindingAction(null)
      setFindingReason('')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The finding could not be updated.')
    } finally {
      setWorking('')
    }
  }

  const submitSignoff = async () => {
    if (!workbookId || !review || !selectedPeriod || !signoffAcknowledgement.trim()) return
    setWorking('signoff')
    try {
      const response = await appApi.signOffCfoClose(workbookId, selectedPeriod, {
        role: signoffRole,
        acknowledgement: signoffAcknowledgement.trim(),
        expected_source_set_token: review.freshness.source_set_token,
        idempotency_key: requestKey(`signoff-${signoffRole}`),
      })
      replaceReview(response.review)
      setSignoffAcknowledgement('')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The sign-off receipt could not be created.')
    } finally {
      setWorking('')
    }
  }

  const attachEvidence = async (input: { file: File; label: string; documentType: string; evidenceId?: string | null }) => {
    if (!workbookId || !review || !selectedPeriod) return
    if (!persisted) {
      setError('Run and pin the close review before attaching evidence.')
      return
    }
    setWorking('evidence')
    setError('')
    try {
      if (input.file.size > 10 * 1024 * 1024) throw new Error('Evidence files are limited to 10 MB.')
      const bytes = new Uint8Array(await input.file.arrayBuffer())
      const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
      const contentHash = Array.from(digest).map((value) => value.toString(16).padStart(2, '0')).join('')
      let binary = ''
      for (let offset = 0; offset < bytes.length; offset += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000))
      }
      const response = await appApi.attachCfoCloseEvidence(workbookId, selectedPeriod, {
        document_type: input.documentType,
        label: input.label,
        content_hash: contentHash,
        content_base64: btoa(binary),
        mime_type: input.file.type || 'application/octet-stream',
        byte_size: input.file.size,
        evidence_id: input.evidenceId || null,
        metadata: { original_filename: input.file.name },
        expected_source_set_token: review.freshness.source_set_token,
        idempotency_key: requestKey(`evidence-${contentHash}`),
      })
      replaceReview(response.review)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Evidence could not be attached.')
    } finally {
      setWorking('')
    }
  }

  const periodOptions = periods.map((period) => ({ id: period.period, label: formatPeriod(period.period) }))
  const pageFacts = useMemo(() => review ? {
    schema_version: review.schema_version,
    period: review.period,
    states: review.states,
    headline: review.headline,
    facets: review.facets,
    next_actions: review.next_actions,
    source_set_token: review.freshness.source_set_token,
  } : null, [review])

  return (
    <CfoInteriorPage
      appApi={appApi}
      workbookId={workbookId}
      navId="close"
      activeSection={CFO_SURFACES.close}
      runtimeProfile={runtimeProfile}
      eyebrow="Month-end close"
      title="Know exactly where the books stand"
      lede="Evidence, close work, reasonableness and sign-off stay separate—so a polished report can never hide what was not checked."
      crumb={<><b>Understand</b> · Close</>}
      asOfLabel={formatPeriod(selectedPeriod)}
      periodOptions={periodOptions}
      periodValue={selectedPeriod}
      onPeriodChange={setSelectedPeriod}
      periodAriaLabel="Close period"
      cacheState={review?.states.stale
        ? { tone: 'failed', label: 'Source changed', title: 'Run the review again against the current source set.' }
        : review
          ? { tone: 'ready', label: persisted ? 'Pinned evidence' : 'Live preview' }
          : null}
      pageLoading={loading}
      pageLoadingLabel="Assembling period-aligned close evidence…"
      copilotSubtitle="grounded in the pinned close review"
      copilotPlaceholder="Ask about this close…"
      copilotIntro="I can summarize blockers and propose next actions from the evidence shown here."
      askPlaceholder="Ask what still needs attention…"
      suggestedPrompts={['What still blocks this close?', 'Which finding could change a decision?', 'Draft questions for the bookkeeper']}
      sourceTables={['accounting_close_periods', 'accounting_close_evidence_items', 'accounting_close_findings', 'accounting_close_tasks', 'accounting_close_task_templates']}
      pageFacts={pageFacts}
      pageFactsFingerprint={review?.freshness.source_set_token || null}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
    >
      {() => (
        <div className="close-control-center">
          {error ? <div className="close-alert bad" role="alert">{error}</div> : null}
          {review?.demo_disclosure?.synthetic_demo ? (
            <div className="close-alert demo" role="status">
              <b>Demo close · synthetic data</b>
              <span> Authored evidence, workpapers, progress, findings, and receipts are shown for the walkthrough. No customer evidence or human attestation is represented.</span>
            </div>
          ) : null}
          {!selectedPeriod && !loading ? (
            <div className="close-empty">
              <h2>No close period is available yet</h2>
              <p>Sync a trial balance, statements and ledger before starting the close.</p>
            </div>
          ) : null}
          {review ? (
            <>
              <section className={`close-hero ${toneFor(review.states.evidence_disposition)}`}>
                <div>
                  <span className="close-kicker">{formatPeriod(review.period.start)} · advisory close control</span>
                  <h2>{review.headline.title}</h2>
                  <p>
                    {review.headline.blocking_count} blocking · {review.headline.open_finding_count} open findings · {review.headline.unavailable_count} unavailable checks
                    {review.states.stale ? ' · source data changed since the pinned review' : ''}
                  </p>
                </div>
                <div className="close-hero-actions">
                  <span className={`close-state-pill ${toneFor(review.states.workflow_status)}`}>{titleCase(review.states.workflow_status)}</span>
                  <button type="button" className="btn primary" disabled={working === 'run' || !review.permissions.can_edit} onClick={runAnalysis}>
                    {working === 'run' ? 'Analyzing…' : persisted ? 'Run close review again' : 'Run and pin close review'}
                  </button>
                </div>
              </section>

              <nav className="close-tabs" aria-label="Close workspace views">
                {TABS.map((item) => (
                  <button type="button" key={item.id} className={tab === item.id ? 'active' : ''} onClick={() => { setFocusedFacet(null); setTab(item.id) }}>
                    {item.label}
                  </button>
                ))}
              </nav>

              {tab === 'command' ? <CommandCenter review={review} onOpen={(next, facet = null) => { setFocusedFacet(facet); setTab(next) }} /> : null}
              {tab === 'checklist' ? <Checklist review={review} working={working} persisted={persisted} onUpdate={updateTask} onCreate={createCustomTask} /> : null}
              {tab === 'reconciliations' ? <Reconciliations review={review} focusedFacet={focusedFacet} onClearFacet={() => setFocusedFacet(null)} persisted={persisted} working={working} onAttach={attachEvidence} /> : null}
              {tab === 'findings' ? (
                <Findings
                  review={review}
                  focusedFacet={focusedFacet}
                  onClearFacet={() => setFocusedFacet(null)}
                  action={findingAction}
                  reason={findingReason}
                  working={working}
                  onAction={setFindingAction}
                  onReason={setFindingReason}
                  onSubmit={submitFindingDisposition}
                  onCancel={() => { setFindingAction(null); setFindingReason('') }}
                />
              ) : null}
              {tab === 'review' ? (
                <ReviewAndSignoff
                  review={review}
                  role={signoffRole}
                  acknowledgement={signoffAcknowledgement}
                  working={working}
                  persisted={persisted}
                  onRole={setSignoffRole}
                  onAcknowledgement={setSignoffAcknowledgement}
                  onSubmit={submitSignoff}
                />
              ) : null}
              {tab === 'history' ? <History history={history} /> : null}

              <footer className="close-limitations">
                <b>Control boundary</b>
                <span>{review.limitations.join(' ')}</span>
                <code title={review.freshness.source_set_token}>{review.freshness.source_set_token.slice(0, 22)}…</code>
              </footer>
            </>
          ) : null}
        </div>
      )}
    </CfoInteriorPage>
  )
}

function CommandCenter({ review, onOpen }: { review: CfoCloseReview; onOpen: (tab: CloseTab, facet?: string | null) => void }) {
  const taskPct = review.tasks.total ? Math.round((review.tasks.completed / review.tasks.total) * 100) : 0
  const openFacet = (facet: CfoCloseReview['facets'][number]) => {
    if (facet.facet === 'workflow_and_control') return onOpen('checklist', facet.facet)
    if (facet.result === 'failed' || facet.result === 'unavailable' || facet.facet === 'analytical_review') {
      return onOpen('findings', facet.facet)
    }
    if (reconciliationFacets.has(facet.facet)) return onOpen('reconciliations', facet.facet)
    return onOpen('checklist', facet.facet)
  }
  return (
    <div className="close-view-stack">
      <section className="close-state-grid" aria-label="Independent close states">
        {[
          ['Evidence', review.states.evidence_disposition, `${review.headline.blocking_count} blocking checks`],
          ['Workflow', review.states.workflow_status, `${review.tasks.completed} of ${review.tasks.total} tasks complete`],
          ['Provider lock', review.states.provider_lock_status, review.provider_lock.close_date ? `Observed ${formatDate(review.provider_lock.close_date)}` : 'Read-only status not supplied'],
          ['Reasonableness', review.states.reasonableness, `${review.findings.filter((finding) => finding.provenance === 'statistical').length} statistical findings`],
        ].map(([label, value, detail]) => (
          <article className="close-state-card" key={label}>
            <span>{label}</span>
            <strong className={toneFor(value)}>{titleCase(value)}</strong>
            <small>{detail}</small>
          </article>
        ))}
      </section>

      <section className="close-panel">
        <header><div><span className="close-kicker">Do these next</span><h3>Highest-priority close actions</h3></div><button type="button" className="btn" onClick={() => onOpen('findings', null)}>Open all findings</button></header>
        {review.next_actions.length ? (
          <div className="close-action-list">
            {review.next_actions.map((action, index) => (
              <article key={action.finding_id}>
                <span className={`close-number ${toneFor(action.severity)}`}>{index + 1}</span>
                <div><b>{action.title}</b><p>{action.recommended_action}</p></div>
                <span className={`close-badge ${toneFor(action.severity)}`}>{titleCase(action.severity)}</span>
              </article>
            ))}
          </div>
        ) : <div className="close-empty compact">No open finding is currently prioritized.</div>}
      </section>

      <section className="close-panel">
        <header><div><span className="close-kicker">Coverage map</span><h3>Every facet of the close</h3></div><span>{taskPct}% workflow complete</span></header>
        <div className="close-facet-grid">
          {review.facets.map((facet) => (
            <button type="button" key={facet.facet} className={`close-facet-card ${toneFor(facet.result)}`} onClick={() => openFacet(facet)} aria-label={`Open ${facet.label} ${titleCase(facet.result)} details`}>
              <div><b>{facet.label}</b><span className={`close-dot ${toneFor(facet.result)}`} /></div>
              <strong>{titleCase(facet.result)}</strong>
              <small>{facet.counts.passed || 0} passed · {facet.counts.failed || 0} failed · {facet.counts.unavailable || 0} unavailable</small>
              <span className="close-facet-open">Open details →</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  )
}

const terminalTaskStatuses = new Set(['complete', 'waived'])

const taskCommentText = (comment: unknown) => {
  if (!comment || typeof comment !== 'object') return ''
  const value = comment as Record<string, unknown>
  return String(value.body || value.text || '').trim()
}

function Checklist({ review, working, persisted, onUpdate, onCreate }: {
  review: CfoCloseReview
  working: string
  persisted: boolean
  onUpdate: (task: CfoCloseTask, status: string) => void
  onCreate: (input: CustomCloseTaskInput) => Promise<boolean>
}) {
  const tasksByKey = new Map(review.tasks.items.map((task) => [task.task_key, task]))
  const [adding, setAdding] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [facet, setFacet] = useState('workflow_and_control')
  const [dueBusinessDay, setDueBusinessDay] = useState(5)
  const [evidenceRequired, setEvidenceRequired] = useState(false)
  const [recurrence, setRecurrence] = useState<'one_time' | 'monthly'>('one_time')
  const submit = async () => {
    if (!title.trim()) return
    const created = await onCreate({
      title: title.trim(),
      description: description.trim(),
      facet,
      due_business_day: dueBusinessDay,
      evidence_required: evidenceRequired,
      recurrence,
    })
    if (!created) return
    setTitle('')
    setDescription('')
    setFacet('workflow_and_control')
    setDueBusinessDay(5)
    setEvidenceRequired(false)
    setRecurrence('one_time')
    setAdding(false)
  }
  return (
    <section className="close-panel close-view-stack">
      <header><div><span className="close-kicker">Operational close</span><h3>{review.tasks.completed} of {review.tasks.total} tasks complete</h3></div><div className="close-header-actions"><span>{review.tasks.overdue} overdue</span><button type="button" className="btn" disabled={!persisted || !review.permissions.can_edit} onClick={() => setAdding((value) => !value)}>{adding ? 'Cancel' : 'Add custom item'}</button></div></header>
      <p className="close-explainer">Start records that ready work is underway. Complete closes the task. Blocked work names the unfinished prerequisite; a waiver closes it only with a documented exception.</p>
      {!persisted ? <p className="close-task-form-note">Run and pin the close review before adding a one-time or recurring checklist item.</p> : null}
      {adding ? (
        <div className="close-custom-task-form" role="group" aria-label="Add custom close item">
          <label>Item name<input autoFocus value={title} maxLength={160} onChange={(event) => setTitle(event.target.value)} placeholder="Review covenant compliance" /></label>
          <label>Recurrence<select value={recurrence} onChange={(event) => setRecurrence(event.target.value as 'one_time' | 'monthly')}><option value="one_time">This close only</option><option value="monthly">Every month from this close</option></select></label>
          <label>Close area<select value={facet} onChange={(event) => setFacet(event.target.value)}><option value="workflow_and_control">Workflow and control</option><option value="cash_and_cards">Cash and cards</option><option value="receivables_and_revenue">Receivables and revenue</option><option value="payables_and_expenses">Payables and expenses</option><option value="payroll_and_taxes">Payroll and taxes</option><option value="operating_balances">Operating balances</option><option value="long_term_balances">Long-term balances</option><option value="cutoff_and_change_control">Cutoff and change control</option><option value="analytical_review">Analytical review</option></select></label>
          <label>Due business day<input type="number" min={1} max={31} value={dueBusinessDay} onChange={(event) => setDueBusinessDay(Math.max(1, Math.min(31, Number(event.target.value) || 1)))} /></label>
          <label className="close-custom-task-description">Description<textarea value={description} maxLength={1000} onChange={(event) => setDescription(event.target.value)} placeholder="What needs to be checked, documented, or approved?" /></label>
          <label className="close-custom-task-check"><input type="checkbox" checked={evidenceRequired} onChange={(event) => setEvidenceRequired(event.target.checked)} /> Evidence support expected</label>
          <div className="close-custom-task-actions"><small>{recurrence === 'monthly' ? 'A fresh task will be created in each later close; prior months remain unchanged.' : `This item applies only to ${formatPeriod(review.period.start)}.`}</small><button type="button" className="btn primary" disabled={!title.trim() || working === 'custom-task'} onClick={submit}>{working === 'custom-task' ? 'Adding…' : 'Add item'}</button></div>
        </div>
      ) : null}
      <div className="close-progress"><span style={{ width: `${review.tasks.total ? (review.tasks.completed / review.tasks.total) * 100 : 0}%` }} /></div>
      <div className="close-task-list">
        {review.tasks.items.map((task) => {
          const busy = working === `task:${task.id}`
          const blockedBy = (task.dependency_keys || [])
            .map((dependency) => tasksByKey.get(dependency))
            .filter((dependency): dependency is CfoCloseTask => Boolean(dependency && !terminalTaskStatuses.has(dependency.status)))
          const latestComment = [...(task.comments || [])].reverse().map(taskCommentText).find(Boolean)
          const terminal = terminalTaskStatuses.has(task.status)
          return (
            <article key={task.task_key} className={task.status === 'complete' || task.status === 'waived' ? 'done' : ''}>
              <button type="button" className="close-task-check" aria-label={`Mark ${task.title} ${task.status === 'complete' ? 'not started' : 'complete'}`} disabled={busy || task.status === 'waived' || (!terminal && blockedBy.length > 0)} onClick={() => onUpdate(task, task.status === 'complete' ? 'not_started' : 'complete')}>
                {task.status === 'complete' ? '✓' : task.status === 'waived' ? '–' : ''}
              </button>
              <div className="close-task-copy"><b>{task.title}</b>{task.custom ? <span className="close-custom-label">Custom · {task.recurrence === 'monthly' ? 'Monthly' : 'This close'}</span> : null}<p>{task.description}</p><small>Business day {task.due_business_day} · due {formatDate(task.due_date)} · {titleCase(task.task_type)}{task.evidence_required ? ' · evidence required' : ''}</small>{blockedBy.length ? <small className="close-task-blocked-by">Blocked by {blockedBy.map((dependency) => dependency.title).join(', ')}</small> : latestComment ? <small className="close-task-comment">{latestComment}</small> : null}</div>
              <div className="close-task-actions">
                <span className={`close-badge ${toneFor(task.status)}`}>{titleCase(task.status)}</span>
                {task.status === 'in_progress' ? <button type="button" onClick={() => onUpdate(task, 'complete')} disabled={busy}>Complete</button> : null}
                {!terminal && task.status !== 'in_progress' && blockedBy.length === 0 ? <button type="button" onClick={() => onUpdate(task, 'in_progress')} disabled={busy}>Start</button> : null}
                {terminal ? <button type="button" onClick={() => onUpdate(task, 'not_started')} disabled={busy}>Reopen</button> : null}
                {!terminal ? <button type="button" onClick={() => onUpdate(task, 'waived')} disabled={busy}>Waive</button> : null}
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}

const reconciliationFacets = new Set(['source_integrity', 'ledger_integrity', 'cash_and_cards', 'receivables_and_revenue', 'payables_and_expenses'])

function Reconciliations({ review, focusedFacet, onClearFacet, persisted, working, onAttach }: {
  review: CfoCloseReview
  focusedFacet: string | null
  onClearFacet: () => void
  persisted: boolean
  working: string
  onAttach: (input: { file: File; label: string; documentType: string; evidenceId?: string | null }) => Promise<void>
}) {
  const evidence = review.evidence.filter((item) => reconciliationFacets.has(item.facet) && (!focusedFacet || item.facet === focusedFacet))
  const focusedLabel = review.facets.find((facet) => facet.facet === focusedFacet)?.label
  return (
    <div className="close-view-stack">
      <section className="close-panel close-view-stack">
        <header><div><span className="close-kicker">Evidence, not a green badge</span><h3>{focusedLabel || 'Reconciliation and integrity detail'}</h3></div><div className="close-header-actions"><span>{evidence.length} controls</span>{focusedFacet ? <button type="button" className="btn" onClick={onClearFacet}>Show all</button> : null}</div></header>
        <EvidenceTable items={evidence} currency={review.company.currency_code} />
      </section>
      <EvidenceUpload items={evidence} persisted={persisted} busy={working === 'evidence'} onAttach={onAttach} />
    </div>
  )
}

function EvidenceUpload({ items, persisted, busy, onAttach }: {
  items: CfoCloseEvidenceItem[]
  persisted: boolean
  busy: boolean
  onAttach: (input: { file: File; label: string; documentType: string; evidenceId?: string | null }) => Promise<void>
}) {
  const [file, setFile] = useState<File | null>(null)
  const [label, setLabel] = useState('')
  const [documentType, setDocumentType] = useState('reconciliation_support')
  const [evidenceId, setEvidenceId] = useState('')
  const submit = async () => {
    if (!file || !label.trim()) return
    await onAttach({ file, label: label.trim(), documentType, evidenceId: evidenceId || null })
    setFile(null)
    setLabel('')
  }
  return (
    <section className="close-panel close-evidence-upload">
      <header><div><span className="close-kicker">Supporting evidence</span><h3>Attach a hashed close document</h3></div><span>{persisted ? 'Stored with the close receipt' : 'Pin the review first'}</span></header>
      <div className="close-upload-grid">
        <label>Document type<select value={documentType} onChange={(event) => setDocumentType(event.target.value)}><option value="reconciliation_support">Reconciliation support</option><option value="bank_statement">Bank statement</option><option value="subledger_tieout">Subledger tie-out</option><option value="rollforward_schedule">Roll-forward schedule</option><option value="tax_or_payroll_support">Tax or payroll support</option></select></label>
        <label>Evidence control<select value={evidenceId} onChange={(event) => setEvidenceId(event.target.value)}><option value="">Period-level evidence</option>{items.map((item) => <option key={item.evidence_id} value={item.evidence_id}>{item.title}</option>)}</select></label>
        <label>Label<input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="July operating account reconciliation" /></label>
        <label>File<input type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} /></label>
      </div>
      <p>The file is SHA-256 verified before storage, limited to 10 MB, and linked to the pinned source token. Extracted document values do not become evidence until separately confirmed.</p>
      <button type="button" className="btn primary" disabled={!persisted || !file || !label.trim() || busy} onClick={submit}>{busy ? 'Attaching…' : 'Attach evidence'}</button>
    </section>
  )
}

function EvidenceTable({ items, currency }: { items: CfoCloseEvidenceItem[]; currency: string }) {
  return (
    <div className="close-evidence-table">
      {items.map((item) => (
        <details key={item.evidence_id} className={toneFor(item.result)}>
          <summary>
            <span className={`close-dot ${toneFor(item.result)}`} />
            <div><b>{item.title}</b><small>{titleCase(item.facet)} · {titleCase(item.assertion)}</small></div>
            {money(item.amount, item.currency_code || currency) ? <span>{money(item.amount, item.currency_code || currency)}</span> : null}
            <span className={`close-badge ${toneFor(item.result)}`}>{titleCase(item.result)}</span>
          </summary>
          <div className="close-evidence-detail">
            <p>{item.explanation}</p>
            <dl><dt>Observed</dt><dd><code>{JSON.stringify(item.observed_value)}</code></dd><dt>Expected</dt><dd><code>{JSON.stringify(item.expected_value)}</code></dd><dt>Detector</dt><dd>{item.detector_version}</dd></dl>
          </div>
        </details>
      ))}
    </div>
  )
}

function Findings({ review, focusedFacet, onClearFacet, action, reason, working, onAction, onReason, onSubmit, onCancel }: {
  review: CfoCloseReview
  focusedFacet: string | null
  onClearFacet: () => void
  action: { id: string; lifecycle: 'resolved' | 'accepted_exception' | 'reopened' } | null
  reason: string
  working: string
  onAction: (value: { id: string; lifecycle: 'resolved' | 'accepted_exception' | 'reopened' }) => void
  onReason: (value: string) => void
  onSubmit: () => void
  onCancel: () => void
}) {
  const findings = [...review.findings]
    .filter((finding) => !focusedFacet || finding.facet === focusedFacet)
    .sort((a, b) => (severityOrder[a.severity] ?? 9) - (severityOrder[b.severity] ?? 9))
  const focusedLabel = review.facets.find((facet) => facet.facet === focusedFacet)?.label
  return (
    <div className="close-view-stack">
      <section className="close-panel">
        <header><div><span className="close-kicker">Reasonableness</span><h3>{focusedLabel || titleCase(review.states.reasonableness)}</h3></div><div className="close-header-actions"><span>Rules and robust statistics · ML not yet authoritative</span>{focusedFacet ? <button type="button" className="btn" onClick={onClearFacet}>Show all</button> : null}</div></header>
        <p className="close-explainer">Findings prioritize review; they do not post entries or decide that a month is closed. Accepted exceptions remain objectively failed or unavailable in the evidence layer.</p>
      </section>
      <section className="close-finding-list">
        {findings.length ? findings.map((finding) => (
          <FindingCard
            key={finding.finding_id}
            finding={finding}
            currency={review.company.currency_code}
            action={action?.id === finding.finding_id ? action : null}
            reason={reason}
            busy={working === `finding:${finding.finding_id}`}
            onAction={onAction}
            onReason={onReason}
            onSubmit={onSubmit}
            onCancel={onCancel}
          />
        )) : <div className="close-empty">No findings are currently surfaced.</div>}
      </section>
    </div>
  )
}

function FindingCard({ finding, currency, action, reason, busy, onAction, onReason, onSubmit, onCancel }: {
  finding: CfoCloseFinding
  currency: string
  action: { id: string; lifecycle: 'resolved' | 'accepted_exception' | 'reopened' } | null
  reason: string
  busy: boolean
  onAction: (value: { id: string; lifecycle: 'resolved' | 'accepted_exception' | 'reopened' }) => void
  onReason: (value: string) => void
  onSubmit: () => void
  onCancel: () => void
}) {
  return (
    <article className={`close-finding ${toneFor(finding.severity)}`}>
      <header><div><span className={`close-badge ${toneFor(finding.severity)}`}>{titleCase(finding.severity)}</span><span className="close-badge neutral">{titleCase(finding.provenance)}</span><span className={`close-badge ${toneFor(finding.lifecycle)}`}>{titleCase(finding.lifecycle)}</span></div>{money(finding.amount, finding.currency_code || currency) ? <strong>{money(finding.amount, finding.currency_code || currency)}</strong> : null}</header>
      <h3>{finding.title}</h3>
      <p>{finding.explanation}</p>
      <div className="close-recommendation"><b>Next action</b><span>{finding.recommended_action}</span></div>
      {finding.disposition_reason ? <small>Disposition: {finding.disposition_reason}</small> : null}
      {action ? (
        <div className="close-inline-form">
          <label>Document why this is {action.lifecycle === 'resolved' ? 'resolved' : action.lifecycle === 'accepted_exception' ? 'an accepted exception' : 'reopened for follow-up'}<textarea value={reason} onChange={(event) => onReason(event.target.value)} /></label>
          <div><button type="button" className="btn primary" disabled={!reason.trim() || busy} onClick={onSubmit}>{busy ? 'Saving…' : 'Save disposition'}</button><button type="button" className="btn" onClick={onCancel}>Cancel</button></div>
        </div>
      ) : finding.lifecycle === 'open' || finding.lifecycle === 'reopened' ? (
        <div className="close-card-actions"><button type="button" className="btn" onClick={() => onAction({ id: finding.finding_id, lifecycle: 'resolved' })}>Mark resolved</button><button type="button" className="btn" onClick={() => onAction({ id: finding.finding_id, lifecycle: 'accepted_exception' })}>Accept exception</button></div>
      ) : (
        <div className="close-card-actions"><button type="button" className="btn" onClick={() => onAction({ id: finding.finding_id, lifecycle: 'reopened' })}>Reopen finding</button></div>
      )}
    </article>
  )
}

function ReviewAndSignoff({ review, role, acknowledgement, working, persisted, onRole, onAcknowledgement, onSubmit }: {
  review: CfoCloseReview
  role: 'preparer' | 'reviewer' | 'attester'
  acknowledgement: string
  working: string
  persisted: boolean
  onRole: (value: 'preparer' | 'reviewer' | 'attester') => void
  onAcknowledgement: (value: string) => void
  onSubmit: () => void
}) {
  return (
    <div className="close-review-grid">
      <section className="close-panel">
        <header><div><span className="close-kicker">Advisory sign-off</span><h3>Create an immutable receipt</h3></div><span>{review.signoffs.length} receipts</span></header>
        {review.headline.blocking_count ? <div className="close-alert warn"><b>{review.headline.blocking_count} blockers remain.</b> Sign-off is permitted by policy, but the receipt and every report will preserve them.</div> : null}
        <div className="close-role-picker">
          {(['preparer', 'reviewer', 'attester'] as const).map((value) => <button type="button" key={value} className={role === value ? 'active' : ''} onClick={() => onRole(value)}>{titleCase(value)}</button>)}
        </div>
        <label className="close-signoff-label">Acknowledgement
          <textarea value={acknowledgement} onChange={(event) => onAcknowledgement(event.target.value)} placeholder="State what you reviewed and how open exceptions should be understood." />
        </label>
        <button type="button" className="btn primary" disabled={!persisted || !acknowledgement.trim() || working === 'signoff'} onClick={onSubmit}>{working === 'signoff' ? 'Signing…' : `Sign as ${role}`}</button>
        {!persisted ? <small>Run and pin the close review before signing.</small> : null}
      </section>
      <section className="close-panel">
        <header><div><span className="close-kicker">Receipts</span><h3>Review history for this source set</h3></div></header>
        <div className="close-receipt-list">
          {review.signoffs.length ? review.signoffs.map((signoff) => (
            <article key={String(signoff.id)}><div><b>{titleCase(signoff.role)}</b><span>{formatDate(signoff.signed_at)}</span></div><p>{signoff.acknowledgement}</p><small>{signoff.open_blocker_count} blockers · {signoff.unavailable_count} unavailable{signoff.separation_of_duties_warning ? ' · separation-of-duties warning' : ''}</small></article>
          )) : <div className="close-empty compact">No sign-off receipt has been created.</div>}
        </div>
        <div className="close-provider-lock"><span>Provider lock</span><strong className={toneFor(review.provider_lock.status)}>{titleCase(review.provider_lock.status)}</strong><small>Read-only · FuseGrid does not lock or post to the provider</small></div>
      </section>
    </div>
  )
}

function History({ history }: { history: CfoCloseHistoryResponse | null }) {
  const periods = history?.periods || []
  const effectiveness = history?.effectiveness
  const matrixPeriods = periods.slice(0, 6)
  const taskRows = Array.from(new Map(
    matrixPeriods.flatMap((period) => period.task_completions || []).map((task) => [task.task_key, task]),
  ).values())
  const latest = effectiveness?.latest
  const change = effectiveness?.change
  const closeDayChange = change?.close_business_days
  const closeDayChangeLabel = closeDayChange == null
    ? 'Need two completed months'
    : closeDayChange < 0
      ? `${Math.abs(closeDayChange)} business day${Math.abs(closeDayChange) === 1 ? '' : 's'} faster`
      : closeDayChange > 0
        ? `${closeDayChange} business day${closeDayChange === 1 ? '' : 's'} slower`
        : 'Same completion day'
  return (
    <div className="close-view-stack">
      <section className="close-panel">
        <header><div><span className="close-kicker">Close effectiveness</span><h3>Is the close getting better?</h3></div>{effectiveness ? <span className={`close-state-pill ${toneFor(effectiveness.trend)}`}>{titleCase(effectiveness.trend)}</span> : null}</header>
        {effectiveness ? <>
          <div className="close-state-grid close-effectiveness-grid">
            <article className="close-state-card"><span>Latest close</span><strong>{latest?.close_business_day == null ? 'Not complete' : `BD+${latest.close_business_day}`}</strong><small>{latest?.period ? formatPeriod(latest.period) : 'No completed period yet'}</small></article>
            <article className="close-state-card"><span>On-time tasks</span><strong>{latest?.on_time_rate == null ? '—' : `${latest.on_time_rate.toFixed(1)}%`}</strong><small>{latest ? `${latest.late_tasks} late task${latest.late_tasks === 1 ? '' : 's'}` : 'Complete a close to measure'}</small></article>
            <article className="close-state-card"><span>Month-over-month</span><strong className={closeDayChange == null ? 'neutral' : closeDayChange < 0 ? 'good' : closeDayChange > 0 ? 'bad' : 'neutral'}>{closeDayChangeLabel}</strong><small>Completed closes only</small></article>
            <article className="close-state-card"><span>Recurring findings</span><strong>{latest?.recurring_findings ?? '—'}</strong><small>{latest?.reopened ? 'Latest period was reopened' : 'No latest-period reopening'}</small></article>
          </div>
          <p className="close-effectiveness-rationale">{effectiveness.rationale}</p>
        </> : <div className="close-empty compact">Close effectiveness is loading.</div>}
      </section>

      <section className="close-panel">
        <header><div><span className="close-kicker">Monthly outcomes</span><h3>Close timing, quality and reopenings</h3></div><span>{periods.length} periods</span></header>
        <div className="close-history-list">
          {periods.length ? periods.map((period) => (
            <article key={String(period.period)}>
              <div><b>{formatPeriod(period.period)}</b><small>{period.close_completed_at ? `Completed ${formatDate(period.close_completed_at)}` : `Updated ${formatDate(period.updated_at)}`}</small></div>
              <span className={`close-badge ${toneFor(period.evidence_disposition)}`}>{titleCase(period.evidence_disposition)}</span>
              <span>{period.completed_tasks}/{period.total_tasks} completed</span>
              <span>{period.close_business_day == null ? 'Close in progress' : `Closed BD+${period.close_business_day}`}</span>
              <span>{period.on_time_rate == null ? 'No timing yet' : `${period.on_time_rate.toFixed(1)}% on time`}</span>
              {period.reopened_at ? <span className="close-badge warn">Reopened</span> : <span>{period.recurring_findings} recurring</span>}
            </article>
          )) : <div className="close-empty compact">Run a close review to begin the durable history.</div>}
        </div>
      </section>

      <section className="close-panel">
        <header><div><span className="close-kicker">Completion by close item</span><h3>Exactly when each item finished each month</h3></div><span>Most recent {matrixPeriods.length} periods</span></header>
        {taskRows.length ? <div className="close-completion-matrix-wrap">
          <table className="close-completion-matrix">
            <thead><tr><th scope="col">Close item</th>{matrixPeriods.map((period) => <th scope="col" key={period.period}>{formatPeriod(period.period)}</th>)}</tr></thead>
            <tbody>{taskRows.map((task) => (
              <tr key={task.task_key}>
                <th scope="row"><b>{task.title}</b><small>{titleCase(task.facet)}</small></th>
                {matrixPeriods.map((period) => {
                  const completion = period.task_completions?.find((item) => item.task_key === task.task_key)
                  if (!completion) return <td key={period.period}>—</td>
                  if (completion.completed_at) return <td key={period.period} className={completion.on_time === false ? 'late' : 'done'}><b>{completion.completion_business_day == null ? 'Done' : `BD+${completion.completion_business_day}`}</b><small>{formatDate(completion.completed_at)}{completion.completion_event_count > 1 ? ` · ${completion.completion_event_count} completions` : ''}</small></td>
                  return <td key={period.period} className="open"><b>{titleCase(completion.status)}</b><small>{completion.due_date ? `Due ${formatDate(completion.due_date)}` : 'No completion recorded'}</small></td>
                })}
              </tr>
            ))}</tbody>
          </table>
        </div> : <div className="close-empty compact">No task completion history is available yet.</div>}
        {history?.methodology ? <small className="close-history-method">Business-day timing excludes weekends. Waivers remain separate from completed work; user completion transitions are retained in the immutable close event log.</small> : null}
      </section>
    </div>
  )
}

export default CfoClosePage
